import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Descriptions,
  Divider,
  Drawer,
  Popconfirm,
  Select,
  Skeleton,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import { useDispatch } from "react-redux";

import {
  deleteAccount,
  fetchAdminAccount,
  resetAdminPassword,
  setAdminStatus,
  updateAdminRole,
} from "../../features/staff/staffThunk";
import { ADMIN_TYPES, SECTION_TAGS, adminTypeLabel } from "./adminTypes";

const formatDate = (value) => (value ? new Date(value).toLocaleString() : "—");

// Popconfirm has no description prop in the installed antd (5.0).
const ConfirmText = ({ title, detail }) => (
  <div style={{ maxWidth: 280 }}>
    <div style={{ fontWeight: 600 }}>{title}</div>
    <div style={{ marginTop: 4 }}>{detail}</div>
  </div>
);

// Details and account actions for one admin: role, password reset,
// enable/disable and delete. onChanged lets the staff list refresh.
const AdminDetailsDrawer = ({ adminId, currentAdminId, onClose, onChanged }) => {
  const dispatch = useDispatch();
  const [admin, setAdmin] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [adminType, setAdminType] = useState(null);
  const [pending, setPending] = useState(null);

  const isSelf = !!adminId && String(adminId) === String(currentAdminId);

  useEffect(() => {
    if (!adminId) return;
    setAdmin(null);
    setLoadError(null);
    dispatch(fetchAdminAccount(adminId))
      .unwrap()
      .then((data) => {
        setAdmin(data.admin);
        setAdminType(data.admin.adminType);
      })
      .catch((error) => setLoadError(error.message));
  }, [adminId, dispatch]);

  // Runs one account action, keeping the drawer and the list in sync.
  const runAction = async (name, thunk, onSuccess) => {
    setPending(name);
    try {
      const data = await dispatch(thunk).unwrap();
      if (data?.admin) {
        setAdmin(data.admin);
        setAdminType(data.admin.adminType);
      }
      onSuccess(data);
      onChanged();
    } catch (error) {
      message.error(error.message);
    } finally {
      setPending(null);
    }
  };

  const handleRoleChange = () =>
    runAction("role", updateAdminRole({ id: adminId, adminType }), () =>
      message.success(`Role changed to ${adminTypeLabel(adminType)}`)
    );

  const handleResetPassword = () =>
    runAction("reset", resetAdminPassword(adminId), (data) => {
      message.success(data.message);
      if (data.admin?.disabled) {
        message.warning("This account is still disabled. Enable it so they can log in.");
      }
    });

  const handleStatus = (disabled) =>
    runAction("status", setAdminStatus({ id: adminId, disabled }), () =>
      message.success(disabled ? "Account disabled" : "Account enabled")
    );

  const handleDelete = () =>
    runAction("delete", deleteAccount(adminId), () => {
      message.success("Admin account deleted");
      onClose();
    });

  const sectionTag = admin && SECTION_TAGS[admin.admin_section_id];
  const newSectionId = ADMIN_TYPES.find((t) => t.value === adminType)?.sectionId;
  const name = admin ? `${admin.firstName} ${admin.lastName}` : "";

  return (
    <Drawer
      title={admin ? name : "Admin details"}
      open={!!adminId}
      onClose={onClose}
      width={520}
      destroyOnClose
    >
      {loadError && <Alert type="error" message={loadError} showIcon />}
      {!admin && !loadError && <Skeleton active />}
      {admin && (
        <>
          {isSelf && (
            <Alert
              style={{ marginBottom: 16 }}
              type="info"
              showIcon
              message="This is your own account. Use Change Password to update your password; another super admin must change your role or status."
            />
          )}

          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Name">{name}</Descriptions.Item>
            <Descriptions.Item label="Email">{admin.email}</Descriptions.Item>
            <Descriptions.Item label="ID">
              <Typography.Text copyable>{admin._id}</Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="Role">
              {sectionTag && <Tag color={sectionTag.color}>{sectionTag.label}</Tag>}
              {adminTypeLabel(admin.adminType)}
            </Descriptions.Item>
            <Descriptions.Item label="Section ID">
              {admin.admin_section_id ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              {admin.disabled ? (
                <Tag color="red">DISABLED</Tag>
              ) : (
                <Tag color="green">ACTIVE</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Failed logins">
              {admin.passwordTries ?? 0} / 3
            </Descriptions.Item>
            <Descriptions.Item label="2FA">
              {admin.mfaEnabled ? "Enabled" : "Not set up"}
            </Descriptions.Item>
            <Descriptions.Item label="Password">
              {admin.newAdmin ? "Temporary — must change at next login" : "Set by admin"}
            </Descriptions.Item>
            <Descriptions.Item label="Created">{formatDate(admin.createdAt)}</Descriptions.Item>
            <Descriptions.Item label="Last updated">
              {formatDate(admin.updatedAt)}
            </Descriptions.Item>
          </Descriptions>

          <Divider orientation="left">Role</Divider>
          <Space wrap>
            <Select
              style={{ width: 220 }}
              value={adminType}
              onChange={setAdminType}
              disabled={isSelf}
              options={ADMIN_TYPES.map(({ value, label }) => ({ value, label }))}
            />
            <Popconfirm
              title={
                <ConfirmText
                  title={`Change ${admin.firstName}'s role to ${adminTypeLabel(adminType)}?`}
                  detail={`Section ID becomes ${newSectionId}. Applies on their next request.`}
                />
              }
              onConfirm={handleRoleChange}
              disabled={isSelf || adminType === admin.adminType}
            >
              <Button
                type="primary"
                loading={pending === "role"}
                disabled={isSelf || adminType === admin.adminType}
              >
                Update role
              </Button>
            </Popconfirm>
          </Space>

          <Divider orientation="left">Account</Divider>
          <Space wrap>
            <Popconfirm
              title={
                <ConfirmText
                  title="Reset password?"
                  detail={`A new temporary password will be emailed to ${admin.email} and they'll be signed out.`}
                />
              }
              onConfirm={handleResetPassword}
              disabled={isSelf}
            >
              <Button loading={pending === "reset"} disabled={isSelf}>
                Reset password
              </Button>
            </Popconfirm>

            <Popconfirm
              title={
                <ConfirmText
                  title={admin.disabled ? "Enable this account?" : "Disable this account?"}
                  detail={
                    admin.disabled
                      ? "They'll be able to log in again, and their failed login count resets."
                      : "They'll be signed out and blocked from logging in."
                  }
                />
              }
              onConfirm={() => handleStatus(!admin.disabled)}
              disabled={isSelf}
            >
              <Button loading={pending === "status"} disabled={isSelf}>
                {admin.disabled ? "Enable account" : "Disable account"}
              </Button>
            </Popconfirm>

            <Popconfirm
              title={
                <ConfirmText title="Delete this admin account?" detail="This can't be undone." />
              }
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={handleDelete}
              disabled={isSelf}
            >
              <Button danger loading={pending === "delete"} disabled={isSelf}>
                Delete account
              </Button>
            </Popconfirm>
          </Space>
        </>
      )}
    </Drawer>
  );
};

export default AdminDetailsDrawer;
